import styled from 'styled-components';
import { BodyMedium, HeadLine1, HeadLine3 } from '../../../../components/typography/styled';

type StatusProp = {
  status?: string,
}

export type PaymentProvider = 'credit' | 'wayforpay' | 'paypal' | 'stripe' | 'crypto' | 'liqpay' | 'fondy' | 'monobank' | 'portmone' | 'default'

type PaymentProviderProp = {
  $provider: PaymentProvider,
}

const paymentProviderBackground = ($provider: PaymentProvider, fallback: string) => {
  switch ($provider) {
    case 'credit':
      return fallback
    case 'wayforpay':
      return '#1A1F71'
    case 'paypal':
      return '#FFC439'
    case 'stripe':
      return '#635BFF'
    case 'crypto':
      return '#F7931A'
    case 'liqpay':
      return '#78B82A'
    case 'fondy':
      return '#202124'
    case 'monobank':
      return '#111111'
    case 'portmone':
      return '#0068A9'
    default:
      return fallback
  }
}

const paymentProviderColor = ($provider: PaymentProvider) => $provider === 'paypal' ? '#003087' : '#FFFFFF'

export const statusColor = (status?: string) => {
  switch (status) {
    case "unpaid":
      return "#FFF7E3";
    case "suspended":
      return "#FFE6E6";
    case "paid":
      return "rgba(34, 155, 97, 0.1);";
    default:
      return "";
  }
}

export const statusColorText = (status?: string) => {
  switch (status) {
    case "unpaid":
      return "#FFB423";
    case "suspended":
      return "#E85048";
    case "paid":
      return "#229B61";
    default:
      return "";
  }
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 10px 20px 30px 30px;
  position: relative;

  .react-loading-skeleton {
    max-width: 100%;
  }

  @media screen and (max-width: 768px) {
    padding: 10px 16px 30px;
  }
`
export const HeadWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width:100%;
  min-width: 0;
  margin-right: 10px;

  @media screen and (max-width: 768px) {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px;
    margin-right: 0;
  }
`

export const LeftSide = styled.div`
  display:flex;
  align-items: center;
  flex-direction: row;
  gap: 10px;
  min-width: 0;

  @media screen and (max-width: 768px) {
    align-items: center;
    flex-wrap: wrap;
  }
`

export const RightHeadContainer = styled.div`
  display:flex;
  margin-right: 10px;
  position: relative;
  flex-shrink: 0;

  @media screen and (max-width: 768px) {
    margin-right: 0;
  }
`

export const InvoiceName = styled(HeadLine1)`
  color:${({theme}) => theme.palette[800]};
  margin-top:10px;
  margin-bottom:10px;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
`
export const RowBodyWrapper = styled.div`
  display:flex;
  flex-direction:row;
  justify-content:space-between;
  gap:30px;
  align-items:flex-start;
  margin-top:20px;
  margin-right:10px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media screen and (max-width: 1050px) {
    flex-direction: column;
    gap: 10px;
    margin-right: 0;
  }
`

export const ColumnBodyWrapper = styled.div`
  display:flex;
  flex-direction:column;
  width:100%;
  min-width: 0;
  gap:10px;
`

export const TopInfoWrapper = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  min-width: 0;

  @media screen and (max-width: 768px) {
    flex-direction: column;

    > * {
      width: 100%;
      flex-basis: auto !important;
    }
  }
`

export const PaymentMethodContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
  gap: 14px;
  box-sizing: border-box;
  width: 320px;
  max-width: 100%;
  background: ${({theme}) => theme.palette[10]};
  border: ${({theme}) => `1px solid ${theme.grey[350]}`};
  border-radius: ${({theme}) => theme.radius.control};
  box-shadow: ${({theme}) => theme.shadows.control};
  flex: none;
  order: 1;
  flex-grow: 0;
  position: sticky;
  top: 20px;

  @media screen and (max-width: 1050px) {
    width: 100%;
    position: static;
  }

`

export const PaymentSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

export const PaymentSummaryLabel = styled.span`
  color: ${({theme}) => theme.palette[800]};
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
`

export const PaymentAmount = styled.span`
  color: ${({theme}) => theme.palette[900]};
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  white-space: nowrap;
`

export const PaymentMethodsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`

export const PaymentMethodButton = styled.button<PaymentProviderProp>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  height: 40px;
  min-height: 40px;
  padding: 8px 14px;
  overflow: hidden;
  appearance: none;
  border: none;
  border-radius: ${({theme}) => theme.radius.control};
  background: ${({$provider, theme}) => paymentProviderBackground($provider, theme.accent[100])};
  box-shadow: none;
  color: ${({$provider}) => paymentProviderColor($provider)};
  font-family: inherit;
  text-align: center;
  cursor: pointer;
  position: relative;
  transition: transform 0.16s ease, filter 0.16s ease, box-shadow 0.16s ease;

  &:hover:not(:disabled) {
    filter: brightness(0.94);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({theme}) => theme.shadows.focus};
  }

  &:disabled {
    cursor: wait;
    opacity: 0.68;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const PaymentMethodContent = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  width: 100%;
  justify-content: center;
`

export const PaymentProviderMark = styled.span<PaymentProviderProp>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({$provider}) => $provider === 'wayforpay' ? '64px' : ['paypal', 'stripe', 'crypto'].includes($provider) ? '22px' : 'auto'};
  height: 22px;
  color: inherit;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  flex: none;
  white-space: nowrap;

  svg {
    display: block;
    width: auto;
    height: 20px;
    fill: currentColor;
  }

  .visa-logo {
    width: 28px;
    fill: #FFFFFF;
  }

  .mastercard-logo {
    width: 30px;
  }

  .paypal-logo {
    height: 22px;
  }

  .stripe-logo,
  .bitcoin-logo {
    height: 20px;
  }
`

export const PaymentMethodLabel = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
`

export const BalancePayBadge = styled.span`
  padding: 2px 8px;
  border-radius: ${({theme}) => theme.radius.xs};
  background: #FFFFFF;
  color: ${({theme}) => theme.accent[200]};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`

export const PaymentButtonSpinner = styled.span`
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  box-sizing: border-box;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: payment-button-spin 0.8s linear infinite;
  position: absolute;
  right: 12px;

  @keyframes payment-button-spin {
    to {
      transform: rotate(360deg);
    }
  }
`

export const NoPaymentMethods = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 16px;
  border: ${({theme}) => `1px dashed ${theme.palette[400]}`};
  border-radius: 8px;
  color: ${({theme}) => theme.palette[700]};
  font-size: 14px;
  line-height: 20px;
  text-align: center;
`

export const ItemTitle = styled(HeadLine3)`
  color: ${({theme}) => theme.primary[600]};
  overflow-wrap: anywhere;
  word-break: break-word;
`

export const InvoiceItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  & + & {
    margin-top: 20px;
  }
`

export const Divider = styled.div`
  border: ${({theme}) => `1px solid ${theme.palette[350]}`};
  width:100%;
  margin-top:10px;
  margin-bottom:10px;
`

export const PriceTotalContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  background-color: ${({theme}) => `${theme.palette[200]}`};
  padding: 6px 10px;
  align-items: center;
  box-sizing: border-box;
  border-radius: 4px;
`

export const PriceTotalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;

`

export const PriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;

  @media screen and (max-width: 768px) {
    flex-wrap: wrap;
  }

`

export const InvoiceStatusContainer = styled.div<StatusProp>`
  display: flex;
  box-sizing: border-box;
  padding:5px 10px;
  border-radius:4px;
  background-color: ${({status}) => statusColor(status)};
`

export const InvoiceStatusText = styled(BodyMedium)<StatusProp>`
  color: ${({status}) => statusColorText(status)};
  text-transform:capitalize;
`
export const BodyWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  /* justify-content: space-between; */
  flex-wrap: wrap;
  min-width: 0;
  /* margin-top: 10px; */
  gap:10px;

  @media screen and (max-width: 768px) {
    flex-direction: column;

    > * {
      width: 100%;
      flex-basis: auto !important;
    }
  }
`
